import { useEffect, useState } from "react";
// Lucide React 아이콘 import
import {
    TicketPercent,
    Banknote,
    Coins,
    UserRoundCheck,
    MousePointerClick,
    Image as ImageIcon,
    Box,
    Ticket,
    CalendarClock // 시간 아이콘 추가
} from "lucide-react";

import { Dropdown, Table } from "omoplay";
import { getMessageStatistics } from "../api/message/message-statistics.api";
import ProjectCalendarSearchBar from "./ProjectCalendarSearchBar";
import moment from "moment/moment";
import { getAllSpots } from "../api/spot/spot.api";
import LoadingBlock from "./LoadingBlock";

const StatisticsMessage = () => {
    const [statistics, setStatistics] = useState({});
    const [totalClickUserTable, setTotalClickUserTable] = useState([]);
    const [entryPoints, setEntryPoints] = useState([]);
    const [entryPointId, setEntryPointId] = useState(undefined);
    const [selectedType, setSelectedType] = useState(null);
    // 조회 중에는 지표 카드·표 대신 스피너를 보여준다(0원/빈 표와 구분되지 않던 문제, 2026-08-05).
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [date, setDate] = useState({
        startDate: moment().subtract(1, "month").format("yyyy-MM-DD"),
        endDate: moment().format("yyyy-MM-DD"),
    });

    // 헤더 정의: 클릭 컨텐츠와 일시를 시각적으로 분리하기 위해 넓이 조정
    const clickUserHeaders = [
        { name: "고객명", align: "center", width: "120px" },
        { name: "혜택명", align: "center" },
        { name: "상세 클릭 내역 (컨텐츠 / 클릭 일시)", align: "left" }
    ];

    // 컨텐츠 타입 아이콘 헬퍼
    const getContentTypeIcon = (typeName) => {
        switch (typeName) {
            case "버튼": return <MousePointerClick className="w-3.5 h-3.5 text-gray-500" />;
            case "이미지": return <ImageIcon className="w-3.5 h-3.5 text-gray-500" />;
            case "아이템": return <Box className="w-3.5 h-3.5 text-gray-500" />;
            case "쿠폰": return <Ticket className="w-3.5 h-3.5 text-gray-500" />;
            default: return <MousePointerClick className="w-3.5 h-3.5 text-gray-500" />;
        }
    };

    useEffect(() => {
        getAllSpots().then(({ data }) => {
            setEntryPoints([{ name: "전체" }, ...data]);
        });
    }, []);

    useEffect(() => {
        setLoading(true);
        setLoadError("");
        getMessageStatistics(selectedType, entryPointId, date?.startDate, date?.endDate).then(({ data }) => {
            setStatistics(data || {});

            setTotalClickUserTable((data.totalClickUsers || []).map(user => {

                return [
                    // 1. 고객명
                    <div className={`flex-center-all h-full font-medium ${user.id ? 'text-primary pointer hover:underline' : ''}`}
                         onClick={() => { if (user.id) window.location.href = `/user/detail?id=${user.id}` }}>
                        {user.name}
                    </div>,

                    // 2. 혜택명 (여러 개일 경우 분리하여 표시)
                    <div className="flex flex-col w-full text-sm">
                        {user.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-stretch border-b border-gray-100 last:border-0 min-h-[48px] hover:bg-gray-50 transition-colors">
                                {/* 좌측: 컨텐츠 정보 */}
                                <div className="flex items-center p-2 gap-3">
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className="truncate font-medium text-gray-700" title={benefit.name}>
                                            {benefit.name}
                                        </span>
                                    </div>
                                </div>

                                {/* 우측: 클릭 시간 (별도 영역 분리) */}
                                <div className="flex flex-col justify-center border-l border-gray-100 bg-gray-50/50 p-2 text-xs text-gray-500">
                                    <div className="flex items-center gap-1 mb-1 text-gray-400">
                                        <CalendarClock className="w-3 h-3" />
                                        <span>사용 일시</span>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {
                                            benefit.usedAt ? moment(benefit.usedAt).format("YYYY-MM-DD HH:mm:ss") : "미사용"
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>,

                    // 3. 상세 클릭 내역 (컨텐츠 + 시간 분리형 레이아웃)
                    <div className="flex flex-col w-full text-sm">
                        {user.clickContents.map((content, idx) => (
                            <div key={idx} className="flex items-stretch border-b border-gray-100 last:border-0 min-h-[48px] hover:bg-gray-50 transition-colors">
                                {/* 좌측: 컨텐츠 정보 */}
                                <div className="flex items-center p-2 gap-3">
                                    {/* 타입 뱃지 */}
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 text-gray-600 rounded text-xs min-w-[65px] justify-center h-fit">
                                        {getContentTypeIcon(content.typeName)}
                                        <span>{content.typeName}</span>
                                    </div>

                                    {/* 이름 및 횟수 */}
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className="truncate font-medium text-gray-700" title={content.name || content.indexName}>
                                            {content.name || content.indexName}
                                        </span>
                                        <span className="text-xs text-primary font-semibold mt-0.5">
                                            총 {content.totalCount}회 클릭
                                        </span>
                                    </div>
                                </div>

                                {/* 우측: 클릭 시간 (별도 영역 분리) */}
                                <div className="flex flex-col justify-center border-l border-gray-100 bg-gray-50/50 p-2 text-xs text-gray-500">
                                    <div className="flex items-center gap-1 mb-1 text-gray-400">
                                        <CalendarClock className="w-3 h-3" />
                                        <span>클릭 일시</span>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {content.clickDate.map((dateStr, dIdx) => (
                                            <span key={dIdx} className="font-mono tracking-tight text-gray-600">
                                                {dateStr}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ];
            }));
        }).catch((e) => {
            setStatistics({});
            setTotalClickUserTable([]);
            setLoadError(e?.message || "조회 중 오류가 발생했습니다.");
        }).finally(() => setLoading(false));
    }, [date, entryPointId, selectedType]);

    /*
      ① '값이 0' 과 '집계 대상이 아예 없음' 을 구분해 안내한다 (2026-09-09).

      종전에는 둘 다 '0원 / 0% / 빈 표' 로 똑같이 보였다. 그래서 자동화를 세팅하지 않은 채널이
      1회성 메시지로 쿠폰을 보내고도 이 화면이 비어 있는 것을 **장애로 신고**했다(2026-09-08).
      이 화면은 자동화 메시지만 집계하므로, 그런 경우 어디를 봐야 하는지까지 알려준다.

      🔴 `hasAutomateSendHistory` 는 '채널에 자동화가 있는가' 가 아니라
         '**이 조회 조건**(기간·자동화·진입점)에 발송 이력이 있었는가' 다.
         그래서 특정 자동화를 고른 경우와 전체를 보는 경우의 문구가 달라야 한다.

      🔴 `=== false` 로 엄격 비교하는 이유 = 두 플래그는 2026-09-09 에 추가된 필드다.
         구 버전 서버가 붙어 있으면 `undefined` 가 되는데, 그때는 안내를 띄우지 않고
         **종전 동작(지표 0 표시)** 으로 남아야 한다. truthy 검사로 바꾸면 구 서버에서 오진한다.
    */
    const emptyState = (() => {
        if (statistics.kakaoChannelLinked === false) {
            return {
                title: "카카오 발신프로필이 연결되지 않았습니다.",
                body: "발송 이력을 조회할 키가 없어 성과를 집계할 수 없습니다. 채널 연동을 완료한 뒤 다시 확인해 주세요.",
            };
        }
        if (statistics.hasAutomateSendHistory === false) {
            return selectedType
                ? {
                    title: `'${selectedType}' 자동화의 발송 이력이 조회 기간에 없습니다.`,
                    body: "조회 기간을 넓히거나, 메시지 종류를 '전체'로 바꿔 확인해 보세요.",
                }
                : {
                    title: "이 채널은 조회 기간에 자동화 메시지 발송 이력이 없습니다.",
                    body: "이 화면은 자동화 메시지만 집계합니다. 1회성 메시지로 보낸 발송·쿠폰 성과는 아래 화면에서 확인하세요.",
                    showLinks: true,
                };
        }
        return null;
    })();

    return (
        <div className="flex-col gap-6 w-full max-w-[1200px] mx-auto pb-10">
            {/* 1. 검색 필터 영역 */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-end gap-4">
                <div className="flex-1 min-w-[300px]">
                    <div className="text-sm font-bold text-gray-600 mb-2">메시지 종류</div>
                    <Dropdown
                        variants="default"
                        style={{ width: "100%" }}
                        titleStyle={{ backgroundColor: "white", height: "42px" }}
                        /*
                          🔴 '전체' 의 value 는 반드시 빈 문자열이다. 라벨("전체")을 값으로 보내면 안 된다.

                          서버는 automateName 을 `automate_message.name` 과 그대로 비교한다
                          (StatisticsMessageController). 종전에는 value 가 "전체" 여서 이 항목을 고르는 순간
                          `name = '전체'` 로 조회 → 매칭 0건 → 모든 지표가 0 이 됐다.
                          라벨이 이미 '전체' 라 "눌러도 아무 일 없을 것" 처럼 보이는데 결과가 비는 형태였다.

                          빈 문자열이면 message-statistics.api.js 의 `if (automateName)` 에서 걸러져
                          파라미터 자체가 붙지 않고, 서버는 null = '전체 자동화' 로 처리한다.
                        */
                        items={[{label: "전체", value: ""}, ...statistics.automateNames?.map(name => ({ value: name, label: name })) ?? []]}
                        onChanged={(items) => setSelectedType(items[0].value)}
                    />
                </div>

                <div className="flex gap-4 items-end">
                    {entryPoints.length > 0 && (
                        <div>
                            <div className="text-sm font-bold text-gray-600 mb-2">진입점</div>
                            <Dropdown
                                variants="default"
                                style={{ width: "180px" }}
                                titleStyle={{ backgroundColor: "white", height: "42px" }}
                                items={entryPoints.map(spot => ({ label: spot.name, value: spot.id }))}
                                onChanged={(items) => setEntryPointId(items[0].value)}
                            />
                        </div>
                    )}
                    <div>
                        <div className="text-sm font-bold text-gray-600 mb-2">조회 기간</div>
                        <ProjectCalendarSearchBar
                            initDateType="month"
                            onConfirm={(dateProps) => setDate(dateProps)}
                        />
                    </div>
                </div>
            </div>

            {/* 집계 범위 상시 명시 (2026-09-09) — 1회성 메시지·모먼트는 이 화면에 포함되지 않는다 */}
            <div className="text-xs text-gray-500 -mt-3 px-1">
                <div>
                    이 화면은 <b className="text-gray-600">자동화 메시지</b>만 집계합니다.
                    1회성 메시지·모먼트 성과는 [통합 성과 대시보드] · [1회성 메시지] 에서 확인하세요.
                </div>
                {/*
                  기간 귀속 축 명시 (2026-09-11).

                  매출·쿠폰 사용률의 모집단은 `benefit_result.issued_at` 으로 거른 '그 기간에 발급된 혜택'
                  이고(BenefitResultService.findAllKakaoUserByAutomateIdList), 사용 여부는 조회 시점까지
                  누적된 `is_used` 를 그대로 본다 = 코호트 + '현재까지' 추적.

                  ⇒ 두 가지 오해가 구조적으로 발생한다.
                     ① 최근 기간은 발급 후 사용까지의 시차 때문에 매출이 낮게 나온다.
                     ② 같은 과거 기간을 다시 조회하면 값이 올라간다(그 뒤에 사용된 건이 더해진다).
                  화면에 축을 적어두지 않으면 "매출이 왜 이렇게 낮냐 / 지난달 값이 왜 바뀌냐" 문의가 된다.

                  🔴 발송 건수·비용은 반대로 '발송 시점'(automate_message_statistics.created_at) 축이다.
                     ROAS 는 두 축을 나눈 값이라는 점도 여기서 읽힌다.

                  🔴 여기에 '대부분 2주 안에 사용된다' 를 쓰지 않는다(2026-09-11 사용자 지시).
                     실측상 사실이긴 하다 — 발급→사용 지연(최근 6개월 3,758건): D+14 누적 99.8%,
                     D+31 이상 0건. 그러나 **그 분포는 고객 행동이 아니라 쿠폰 유효기간의 결과**다.
                     발급 시 유효기간을 보통 7·14·30일로 세팅하므로 그 이후 사용 처리가 애초에 불가능하다
                     (`benefit_result.expired_date`). ⇒ '2주' 는 채널 설정에 따라 달라지는 값이라
                     화면에 고정 문구로 박으면 틀린 안내가 된다.
                     게다가 화면에 2주로 잘라 보는 기능이 없어 사용자가 쓸 수도 없다.
                     ⇒ 축만 밝히고 끝낸다. D+7/14/30 구간 분리도 같은 이유로 미착수 확정.
                     근거·정정 = _ai-runs/2026-09-11_003_leftover_calendar_key_sales_attribution_in_list/007
                */}
                <div className="mt-1">
                    <b className="text-gray-600">매출 · 쿠폰 사용률</b>은 선택한 기간에 <b className="text-gray-600">발급된</b> 혜택이
                    <b className="text-gray-600"> 오늘까지</b> 사용된 결과입니다.
                </div>
            </div>

            {(loading || loadError) ? (
                <LoadingBlock error={loadError} />
            ) : emptyState ? (
                <div className="w-full flex flex-col items-center justify-center gap-3 py-16 bg-white rounded-xl border border-gray-100">
                    <div className="text-sm font-semibold text-gray-700">{emptyState.title}</div>
                    <div className="text-xs text-gray-500 text-center px-4 break-words">{emptyState.body}</div>
                    {emptyState.showLinks && (
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-primary font-semibold pointer hover:underline"
                                  onClick={() => { window.location.href = "/dashboard" }}>
                                통합 성과 대시보드로 이동
                            </span>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs text-primary font-semibold pointer hover:underline"
                                  onClick={() => { window.location.href = "/volatility-message" }}>
                                1회성 메시지로 이동
                            </span>
                        </div>
                    )}
                </div>
            ) : (
              <>
            {/* 2. 대시보드 카드 영역 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* 매출액 */}
                <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-primary flex flex-col justify-between min-h-[160px]">
                    <div>
                        <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <Banknote className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-gray-600">전체 매출액</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-800 mt-2">
                            {statistics.totalSales?.toLocaleString()}원
                        </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                        ROAS <span className="text-green-600 font-bold ml-1">{statistics.roas?.toLocaleString()}%</span>
                    </div>
                </div>

                {/* 쿠폰 사용률 */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <TicketPercent className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-gray-600">쿠폰 사용률</span>
                        </div>
                        <div className="text-4xl font-extrabold text-gray-800 mt-2">
                            {statistics.usedCountRate || 0}%
                        </div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500 bg-gray-50 p-2 rounded mt-4">
                        <span>사용 <b>{statistics.totalUsedCount?.toLocaleString()}</b>회</span>
                        <span className="text-gray-300">|</span>
                        <span>발급 수 <b>{statistics.totalBenefitCount?.toLocaleString()}</b>회</span>
                    </div>
                </div>

                {/* 발송 비용 */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <Coins className="w-5 h-5 text-yellow-600" />
                            <span className="font-semibold text-gray-600">발송 비용</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-800 mt-2">
                            {statistics.totalMessagePrice?.toLocaleString()}원
                        </div>
                    </div>
                    {/*
                      🔴 '발송 건수' 와 '수신 고객 수' 를 함께 보여준다 (2026-09-11).

                      발송 비용은 **건수** 기준이다. 자동화는 같은 사람에게 반복해서 나가므로
                      (우천·생일·재방문 유도) 인원만 보여 주면 "왜 500명인데 2,000건 값이 나오냐" 가 된다.
                      종전에는 서버가 인원에 단가를 곱했고, 그래서 비용이 실제 청구액보다 낮게 나왔다.

                      `totalSuccessSendCount` 는 2026-09-11 에 추가된 필드다. 구 서버가 붙어 있으면
                      `undefined` 이므로 그때는 **종전 표시(인원만)** 로 남는다 — 없는 값을 0건으로
                      그리면 "발송이 사라졌다" 는 더 나쁜 오해가 된다.
                    */}
                    <div className="mt-4 text-sm text-gray-500">
                        {statistics.totalSuccessSendCount === undefined ? (
                            <>총 발송 인원 <b className="ml-1">{statistics.totalSendCount?.toLocaleString()}명</b></>
                        ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span>총 발송 <b className="text-gray-700">{statistics.totalSuccessSendCount.toLocaleString()}건</b></span>
                                <span className="text-gray-300">|</span>
                                <span>수신 고객 <b className="text-gray-700">{statistics.totalSendCount?.toLocaleString()}명</b></span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 보조 지표 */}
            <div className="flex justify-end gap-8 text-sm text-gray-500 px-2">
                <div className="flex items-center gap-2">
                    <UserRoundCheck className="w-4 h-4 text-gray-400" />
                    <span>재방문 전환 비용: <b>{statistics.revisitPerPrice?.toLocaleString()}원</b></span>
                </div>
                <div className="flex items-center gap-2">
                    <MousePointerClick className="w-4 h-4 text-gray-400" />
                    <span>총 반응 고객 수: <b>{statistics.clickCountPerUser}명</b></span>
                </div>
            </div>

            {/* 3. 상세 데이터 테이블 */}
            <div className="flex-col gap-3 mt-2">
                <div className="flex items-center justify-between">
                    <div className="font-bold text-lg text-gray-700">상세 클릭 데이터</div>
                    <span className="text-xs text-gray-400">메시지 내 컨텐츠 클릭 내역을 확인할 수 있습니다.</span>
                </div>
                <Table
                    headers={clickUserHeaders}
                    data={totalClickUserTable}
                    pagination={{ pageSize: 5 }}
                />
            </div>
              </>
            )}
        </div>
    );
};
export default StatisticsMessage;